import { logWarn } from "src/utils/logger";
import { PersistentMessageState } from "src/components/Chat/redux/reducer";
import { HondaTracking, UtmTracking } from "Types/api/Tracking";

const HONDA_KEY = "_honda_tracking";
const UTM_KEY = "_utm_tracking";

export interface EnvironmentKeys {
    slug: string;
    env: string;
}

export interface OldLocalDealState {
    dealUuid: string;
    garageCreated: boolean;
    verified: boolean;
    uuid: string;
    isAuthed: boolean;
}

export interface LocalStorageState {
    uuid: string;
    garageCreated: boolean;
    isAuthed: boolean;
}

export interface LocalAccountSettingsState {
    eTag?: string;
    data?: any;
}

const storageKey = (slug: string, env: string) => `onshop_${slug}_${env}`;

const getLocalStorageFromKey = (storageKey: string): string | null => {
    try {
        return window.localStorage?.getItem(storageKey);
    } catch (e) {
        logWarn(e);
        return null;
    }
};

const setLocalStorageFromKey = (storageKey: string, data: string): void => {
    try {
        window.localStorage?.setItem(storageKey, data);
    } catch (e) {
        logWarn(e);
    }
};

const getHondaTrackingData = ({ slug, env }: EnvironmentKeys): HondaTracking | null => {
    const itemFromStorage = getLocalStorageFromKey(`${storageKey(slug, env)}${HONDA_KEY}`);
    if (!itemFromStorage) {
        return null;
    }

    return JSON.parse(itemFromStorage);
};

const setHondaTrackingData = ({ slug, env }: EnvironmentKeys, hondaTracking: HondaTracking): void => {
    return setLocalStorageFromKey(`${storageKey(slug, env)}${HONDA_KEY}`, JSON.stringify(hondaTracking));
};

const getUtmTrackingData = ({ slug, env }: EnvironmentKeys): UtmTracking | null => {
    const itemFromStorage = getLocalStorageFromKey(`${storageKey(slug, env)}${UTM_KEY}`);
    if (!itemFromStorage) {
        return null;
    }

    return JSON.parse(itemFromStorage);
};

const setUtmTrackingData = ({ slug, env }: EnvironmentKeys, utmTracking: UtmTracking): void => {
    return setLocalStorageFromKey(`${storageKey(slug, env)}${UTM_KEY}`, JSON.stringify(utmTracking));
};

const setLocalDealState = ({ slug, env, uuid, garageCreated, isAuthed }: EnvironmentKeys & LocalStorageState): void => {
    try {
        // Not for security, just to deter anybody from playing around with the values in local storage
        const obfuscatedValue = btoa(JSON.stringify({ uuid, garageCreated, isAuthed }));

        window.localStorage?.setItem(storageKey(slug, env), obfuscatedValue);
    } catch (e) {
        logWarn(e);
    }
};

const getLocalDealState = ({ slug, env }: EnvironmentKeys): LocalStorageState | null => {
    try {
        const itemFromStorage = window.localStorage?.getItem(storageKey(slug, env)) ?? "";
        const deobfuscatedValue = itemFromStorage.includes("{") // angle bracket used in json is not a valid Base64 char
            ? itemFromStorage // handle previous implementation where no obfuscation was used
            : atob(itemFromStorage) || "{}"; // fallback to minimum valid json
        const localState: Partial<OldLocalDealState> | undefined = JSON.parse(deobfuscatedValue);

        if (!localState || (!localState.dealUuid && !localState.uuid)) {
            return null;
        }

        return {
            uuid: localState.dealUuid ?? localState.uuid!,
            garageCreated: localState.garageCreated ?? true, // For legacy support assume anybody missing this value created a garage (previously this value wasn't required)
            isAuthed: localState.verified ?? localState.isAuthed ?? true, // Previously only verified users had their deal saved to local storage so we default this to true
        };
    } catch (e) {
        logWarn(e);

        return null;
    }
};

const setLocalAccountSettingsState = ({ slug, env, eTag, data }: EnvironmentKeys & LocalAccountSettingsState): void => {
    try {
        window.localStorage?.setItem(`${storageKey(slug, env)}_account_settings`, JSON.stringify({ eTag, data }));
    } catch (e) {
        if (e.name === "SecurityError") {
            return;
        }

        if (e.name === "QuotaExceededError") {
            logWarn("QuotaExceededError: Failed to save account settings to local storage", { account: slug });
            return;
        }

        logWarn(e);
    }
};

const getLocalAccountSettingsState = ({ slug, env }: EnvironmentKeys): LocalAccountSettingsState | null => {
    try {
        const itemFromStorage = window.localStorage?.getItem(`${storageKey(slug, env)}_account_settings`);

        if (!itemFromStorage) {
            return null;
        }

        const accountSettings: Partial<LocalAccountSettingsState> = JSON.parse(itemFromStorage);

        if (!accountSettings || !accountSettings.eTag || !accountSettings.data) {
            return null;
        }

        return accountSettings;
    } catch (e) {
        if (e.name === "SecurityError") {
            return null;
        }

        logWarn(e);

        return null;
    }
};

const forgetLocalDealState = ({ slug, env }: EnvironmentKeys): void => {
    try {
        window.localStorage.removeItem(storageKey(slug, env));
    } catch (e) {
        logWarn(e);
    }
};

const rememberDealFromMagicLink = (accountSlug: string, env: string, uuid: string) => {
    setLocalDealState({
        uuid,
        slug: accountSlug,
        env,
        garageCreated: true,
        isAuthed: true,
    });
};

const setChatLocalState = (state: PersistentMessageState, env: string, slug: string): void => {
    try {
        window.localStorage?.setItem(`${storageKey(slug, env)}_chat_state`, JSON.stringify(state));
    } catch (e) {
        if (e.name === "SecurityError") {
            return;
        }

        if (e.name === "QuotaExceededError") {
            logWarn("QuotaExceededError: Failed to save chat to local storage");
            return;
        }

        logWarn(e);
    }
};

const getChatLocalState = ({ slug, env }: EnvironmentKeys): PersistentMessageState | {} => {
    try {
        const itemFromStorage = window.localStorage?.getItem(`${storageKey(slug, env)}_chat_state`);

        if (!itemFromStorage) {
            return {};
        }

        return JSON.parse(itemFromStorage) as PersistentMessageState;
    } catch (e) {
        logWarn(e);

        return {};
    }
};

const forgetChatLocalState = ({ slug, env }: EnvironmentKeys): void => {
    try {
        window.localStorage.removeItem(`${storageKey(slug, env)}_chat_state`);
    } catch (e) {
        logWarn(e);
    }
};

export {
    setLocalDealState,
    getLocalDealState,
    forgetLocalDealState,
    rememberDealFromMagicLink,
    getLocalAccountSettingsState,
    setLocalAccountSettingsState,
    setChatLocalState,
    getChatLocalState,
    forgetChatLocalState,
    getHondaTrackingData,
    setHondaTrackingData,
    getUtmTrackingData,
    setUtmTrackingData,
};
